import type { StructuredSpec, SpecNode, SpecEdge } from '@/types/recipe'

type RecipeAction = Record<string, unknown> & {
  id?: string
  type?: string
  output_key?: string
  tool?: string
  recipe?: string
  skill?: string
  llm_prompt?: string
}

type RecipeForm = Record<string, unknown> & {
  id?: string
  title?: string
  fields?: Array<Record<string, unknown>>
}

type RecipeDefinition = Record<string, unknown> & {
  id?: string
  recipe_id?: string
  name?: string
  version?: string
  description?: string
  mode?: string
  forms?: RecipeForm[]
  actions?: RecipeAction[]
}

const RECIPE_OFFSET_Y = 280
const FORM_SPACING_Y = 110
const ACTION_SPACING_X = 260
const RECIPE_START_X = 40
const FORM_COLUMN_X = RECIPE_START_X
const ACTION_START_X = 340
const OUTPUT_OFFSET_X = 260
const CONTENT_OFFSET_Y = 40

function compact(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined
  }

  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

function inferActionNodeType(actionType: string | undefined): SpecNode['type'] {
  switch ((actionType ?? '').toLowerCase()) {
    case 'question':
      return 'question'
    case 'llm_call':
      return 'llm'
    case 'condition':
    case 'condition_branch':
    case 'branch':
      return 'condition'
    case 'code_execution':
      return 'code'
    case 'recipe_call':
      return 'recipe'
    case 'data_query':
    case 'data_write':
      return 'data'
    case 'tool_call':
    case 'skill':
    default:
      return 'mcp'
  }
}

function buildFormNode(recipeId: string, form: RecipeForm, index: number, recipeIndex: number): SpecNode {
  const fields = Array.isArray(form.fields) ? form.fields : []
  const fieldNames = fields
    .map((field) => compact(field?.name))
    .filter((value): value is string => Boolean(value))

  return {
    id: `${recipeId}:form:${compact(form.id) ?? index + 1}`,
    type: 'io',
    label: compact(form.title) ?? compact(form.id) ?? `Form ${index + 1}`,
    config: {
      direction: 'input',
      format: 'form',
      description:
        fieldNames.length > 0
          ? fieldNames.join(', ')
          : 'Entrée utilisateur',
      fields,
    },
    position: {
      x: FORM_COLUMN_X,
      y: recipeIndex * RECIPE_OFFSET_Y + index * FORM_SPACING_Y,
    },
  }
}

function buildActionNode(recipeId: string, action: RecipeAction, index: number, recipeIndex: number): SpecNode {
  const actionId = compact(action.id) ?? `${recipeId}_action_${index + 1}`
  const actionType = compact(action.type) ?? 'action'
  const nodeType = inferActionNodeType(actionType)

  if (nodeType === 'question') {
    return { id: `${recipeId}:action:${actionId}`, type: 'question', label: actionId, config: { questions: action.questions, provider: action.provider, output_key: action.output_key, action_type: actionType }, position: { x: ACTION_START_X + index * ACTION_SPACING_X, y: recipeIndex * RECIPE_OFFSET_Y + CONTENT_OFFSET_Y } }
  }

  if (nodeType === 'llm') {
    return {
      id: `${recipeId}:action:${actionId}`,
      type: 'llm',
      label: actionId,
      config: {
        prompt: compact(action.llm_prompt) ?? '',
        output_key: compact(action.output_key),
        action_type: actionType,
      },
      position: {
        x: ACTION_START_X + index * ACTION_SPACING_X,
        y: recipeIndex * RECIPE_OFFSET_Y + CONTENT_OFFSET_Y,
      },
    }
  }

  return {
    id: `${recipeId}:action:${actionId}`,
    type: nodeType,
    label:
      compact(action.tool) ??
      compact(action.recipe) ??
      compact(action.skill) ??
      actionId,
    config: {
      tools: [
        compact(action.tool) ??
          compact(action.recipe) ??
          compact(action.skill) ??
          actionType,
      ].filter((value): value is string => Boolean(value)),
      server: compact(action.tool) ?? compact(action.skill) ?? actionType,
      connected: true,
      action_id: actionId,
      action_type: actionType,
      output_key: compact(action.output_key),
      recipe_target: compact(action.recipe),
    },
    position: {
      x: ACTION_START_X + index * ACTION_SPACING_X,
      y: recipeIndex * RECIPE_OFFSET_Y + CONTENT_OFFSET_Y,
    },
  }
}

function buildOutputNode(recipeId: string, recipeName: string, actions: RecipeAction[], recipeIndex: number): SpecNode {
  const outputKeys = actions
    .map((action) => compact(action.output_key))
    .filter((value): value is string => Boolean(value))

  return {
    id: `${recipeId}:output`,
    type: 'io',
    label: `${recipeName} Output`,
    config: {
      direction: 'output',
      format: 'result',
      description:
        outputKeys.length > 0 ? outputKeys.join(', ') : 'Résultats agrégés',
      outputs: outputKeys,
    },
    position: {
      x:
        ACTION_START_X +
        Math.max(actions.length - 1, 0) * ACTION_SPACING_X +
        OUTPUT_OFFSET_X,
      y: recipeIndex * RECIPE_OFFSET_Y + CONTENT_OFFSET_Y,
    },
  }
}

function buildRecipeEdges(
  recipeId: string,
  forms: RecipeForm[],
  actions: RecipeAction[],
): SpecEdge[] {
  const edges: SpecEdge[] = []
  const firstActionId = compact(actions[0]?.id)

  if (firstActionId) {
    forms.forEach((form, index) => {
      const formId = compact(form.id) ?? String(index + 1)
      edges.push({
        id: `${recipeId}:edge:form:${formId}:action:${firstActionId}`,
        source: `${recipeId}:form:${formId}`,
        target: `${recipeId}:action:${firstActionId}`,
      })
    })
  }

  for (let index = 1; index < actions.length; index += 1) {
    const previousActionId = compact(actions[index - 1]?.id)
    const actionId = compact(actions[index]?.id)
    if (!previousActionId || !actionId) {
      continue
    }

    edges.push({
      id: `${recipeId}:edge:action:${previousActionId}:action:${actionId}`,
      source: `${recipeId}:action:${previousActionId}`,
      target: `${recipeId}:action:${actionId}`,
      label: compact(actions[index - 1]?.output_key),
    })
  }

  for (const consumer of actions) {
    if (consumer.type !== 'decision') continue
    const reference = compact(consumer.input) ?? compact(consumer.selector_input)
    const match = reference?.match(/^\{\{\s*([a-zA-Z0-9_-]+)\.([a-zA-Z0-9_-]+)\.choice\s*\}\}$/)
    if (!match) continue
    const producer = actions.find((action) => action.type === 'question' && action.output_key === match[1])
    if (!producer?.id || !consumer.id) continue
    const source = `${recipeId}:action:${producer.id}`
    const target = `${recipeId}:action:${consumer.id}`
    const existing = edges.find((edge) => edge.source === source && edge.target === target)
    if (existing) existing.label = `${match[1]}.${match[2]}.choice`
    else edges.push({ id: `${recipeId}:question:${producer.id}:${consumer.id}`, source, target, label: `${match[1]}.${match[2]}.choice` })
  }

  const lastActionId = compact(actions[actions.length - 1]?.id)
  if (lastActionId) {
    edges.push({
      id: `${recipeId}:edge:action:${lastActionId}:output`,
      source: `${recipeId}:action:${lastActionId}`,
      target: `${recipeId}:output`,
    })
  }

  return edges
}

function normalizeRecipeDefinition(input: Record<string, unknown>, index: number): RecipeDefinition {
  const recipeId =
    compact(input.recipe_id) ??
    compact(input.id) ??
    compact(input.name) ??
    `recipe_${index + 1}`

  return {
    ...input,
    id: recipeId,
    name: compact(input.name) ?? recipeId,
    forms: Array.isArray(input.forms)
      ? (input.forms as RecipeForm[])
      : [],
    actions: Array.isArray(input.actions)
      ? (input.actions as RecipeAction[])
      : [],
  }
}

export function buildStructuredSpecFromRecipeDefinitions(
  rawDefinitions: Array<Record<string, unknown>>,
): StructuredSpec {
  const definitions = rawDefinitions
    .filter((value): value is Record<string, unknown> => Boolean(value) && typeof value === 'object')
    .map(normalizeRecipeDefinition)

  const nodes: SpecNode[] = []
  const edges: SpecEdge[] = []

  definitions.forEach((definition, recipeIndex) => {
    const recipeId = compact(definition.id) ?? `recipe_${recipeIndex + 1}`
    const recipeName = compact(definition.name) ?? recipeId
    const forms = definition.forms && definition.forms.length > 0
      ? definition.forms
      : [{ id: 'main_form', title: recipeName, fields: [] }]
    const actions = definition.actions ?? []

    forms.forEach((form, index) => {
      nodes.push(buildFormNode(recipeId, form, index, recipeIndex))
    })

    actions.forEach((action, index) => {
      nodes.push(buildActionNode(recipeId, action, index, recipeIndex))
    })

    if (actions.length > 0) {
      nodes.push(buildOutputNode(recipeId, recipeName, actions, recipeIndex))
    }

    edges.push(...buildRecipeEdges(recipeId, forms, actions))
  })

  return {
    nodes,
    edges,
  }
}
